/* ── Clewelle Theme — Main JS ────────────────────────────────────────────── */

// ── Cart API ──────────────────────────────────────────────────────────────────

const Cart = {
  async get() {
    return fetch('/cart.js').then(r => r.json());
  },
  async add(id, quantity = 1) {
    const r = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id, quantity }),
    });
    if (!r.ok) {
      const e = await r.json();
      throw new Error(e.description || 'Could not add to cart');
    }
    return r.json();
  },
  async change(key, quantity) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity }),
    }).then(r => r.json());
  },
};

// Renders money the way Liquid's `money` filter does, by expanding the store's
// own money_format string (passed in from layout/theme.liquid). This used to be
// an Intl.NumberFormat call, which produced "₹1,290" in the cart drawer and on
// the product page while every product card next to them said "Rs. 1,090.00".
function groupDigits(amount, thousands, decimal) {
  const [whole, frac] = amount.split('.');
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
  return frac ? grouped + decimal + frac : grouped;
}

function formatMoney(cents) {
  const format = window.Clewelle?.moneyFormat || 'Rs. {{amount}}';
  const rupees = cents / 100;
  return format.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, token) => {
    switch (token) {
      case 'amount_no_decimals':
        return groupDigits(rupees.toFixed(0), ',', '.');
      case 'amount_with_comma_separator':
        return groupDigits(rupees.toFixed(2), '.', ',');
      case 'amount_no_decimals_with_comma_separator':
        return groupDigits(rupees.toFixed(0), '.', ',');
      default:
        return groupDigits(rupees.toFixed(2), ',', '.');
    }
  });
}

// ── Cart Drawer ───────────────────────────────────────────────────────────────

class CartDrawer {
  constructor() {
    this.drawer = document.querySelector('.cart-drawer');
    this.overlay = document.querySelector('.cart-overlay');
    if (!this.drawer) return;
    this._bindEvents();
  }

  open() {
    this.drawer.classList.add('open');
    this.overlay.classList.add('open');
    document.body.style.overflow = 'hidden';
    this._refresh();
  }

  close() {
    this.drawer.classList.remove('open');
    this.overlay.classList.remove('open');
    document.body.style.overflow = '';
  }

  _bindEvents() {
    this.overlay?.addEventListener('click', () => this.close());
    document.querySelector('.cart-drawer-close')?.addEventListener('click', () => this.close());
    document.querySelectorAll('[data-open-cart]').forEach(el =>
      el.addEventListener('click', e => { e.preventDefault(); this.open(); })
    );
  }

  async _refresh() {
    const cart = await Cart.get();
    this._render(cart);
    this._updateCount(cart.item_count);
  }

  _render(cart) {
    const body = this.drawer.querySelector('.cart-drawer-body');
    const footer = this.drawer.querySelector('.cart-drawer-footer');

    // Update header count
    const titleCount = this.drawer.querySelector('.cart-drawer-count');
    if (titleCount) titleCount.textContent = `(${cart.item_count})`;

    if (cart.item_count === 0) {
      body.innerHTML = `
        <div class="cart-empty-state">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" style="width:4rem;height:4rem;color:#e5e7eb;margin:0 auto 1rem"><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>
          <p>Your cart is empty</p>
          <a href="/collections/all" class="btn btn-outline btn-sm">Continue Shopping</a>
        </div>`;
      if (footer) footer.style.display = 'none';
      return;
    }

    if (footer) footer.style.display = '';
    body.innerHTML = cart.items.map(item => `
      <div class="cart-item" data-key="${item.key}">
        <img class="cart-item-img" src="${item.image}" alt="${item.product_title}" width="72" height="72">
        <div class="cart-item-info">
          <p class="cart-item-title">${item.product_title}</p>
          ${item.variant_title && item.variant_title !== 'Default Title'
            ? `<p class="cart-item-variant">${item.variant_title}</p>` : ''}
          <p class="cart-item-price">${formatMoney(item.final_line_price)}</p>
          <div class="cart-item-qty">
            <button onclick="window.cartDrawer.changeQty('${item.key}', ${item.quantity - 1})" aria-label="Decrease">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="width:.875rem;height:.875rem"><line x1="5" y1="12" x2="19" y2="12"/></svg>
            </button>
            <span>${item.quantity}</span>
            <button onclick="window.cartDrawer.changeQty('${item.key}', ${item.quantity + 1})" aria-label="Increase">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="width:.875rem;height:.875rem"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            </button>
            <button class="cart-item-remove" onclick="window.cartDrawer.changeQty('${item.key}', 0)" aria-label="Remove">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="width:.875rem;height:.875rem"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6M9 6V4h6v2"/></svg>
            </button>
          </div>
        </div>
      </div>`).join('');

    const amountEl = footer?.querySelector('.cart-total-amount');
    if (amountEl) amountEl.textContent = formatMoney(cart.total_price);
  }

  async changeQty(key, qty) {
    const cart = await Cart.change(key, qty);
    this._render(cart);
    this._updateCount(cart.item_count);
  }

  _updateCount(count) {
    document.querySelectorAll('.cart-count').forEach(el => {
      el.textContent = count || '';
      el.style.display = count > 0 ? 'flex' : 'none';
    });
  }
}

// ── Add to Cart ───────────────────────────────────────────────────────────────

async function addToCart(variantId, qty = 1, btn, redirectTo) {
  if (btn) {
    btn.disabled = true;
    const orig = btn.innerHTML;
    btn.innerHTML = '<span class="loading-spinner"></span>';
    btn.dataset.origHtml = orig;
  }
  let navigating = false;
  try {
    await Cart.add(variantId, qty);
    if (redirectTo) {
      navigating = true;
      window.location.href = redirectTo;
    } else {
      window.cartDrawer?.open();
    }
  } catch (err) {
    alert(err.message);
  } finally {
    // Leave the spinner up while the browser navigates away.
    if (btn && !navigating) {
      btn.disabled = false;
      btn.innerHTML = btn.dataset.origHtml || 'Add to Cart';
    }
  }
}

// Card quick-add opens a size picker instead of adding. It used to carry
// `first_available_variant` and add it outright, so every one-click add from a
// product row was size 38 whether the customer wanted that size or not.
function closeCardSizes(except) {
  document.querySelectorAll('[data-card-sizes]').forEach(p => {
    if (p === except) return;
    p.hidden = true;
    p.closest('.product-card-img-wrap')
      ?.querySelector('[data-card-sizes-toggle]')?.setAttribute('aria-expanded', 'false');
  });
}

document.addEventListener('click', e => {
  const toggle = e.target.closest('[data-card-sizes-toggle]');
  if (toggle) {
    e.preventDefault();
    const panel = toggle.closest('.product-card-img-wrap')?.querySelector('[data-card-sizes]');
    if (!panel) return;
    const opening = panel.hidden;
    closeCardSizes(panel);
    panel.hidden = !opening;
    toggle.setAttribute('aria-expanded', String(opening));
    return;
  }

  const btn = e.target.closest('[data-quick-add]');
  if (!btn) {
    // Any click outside an open picker dismisses it.
    if (!e.target.closest('[data-card-sizes]')) closeCardSizes();
    return;
  }
  e.preventDefault();
  const id = btn.dataset.quickAdd;
  if (id) addToCart(id, 1, btn).then(() => closeCardSizes());
});

document.addEventListener('keydown', e => {
  if (e.key === 'Escape') closeCardSizes();
});

// ── Hero Carousel ─────────────────────────────────────────────────────────────

class HeroCarousel {
  constructor(el) {
    this.el = el;
    this.track = el.querySelector('[data-carousel-track]');
    this.slides = el.querySelectorAll('[data-slide]');
    this.dots = el.querySelectorAll('[data-dot]');
    this.prev = el.querySelector('[data-carousel-prev]');
    this.next = el.querySelector('[data-carousel-next]');
    this.current = 0;
    this.total = this.slides.length;
    this.autoplay = el.dataset.autoplay !== 'false';
    this.interval = parseInt(el.dataset.interval) || 4000;
    this.timer = null;
    this.paused = false;

    if (this.total <= 1) return;
    this._bind();
    if (this.autoplay) this._start();
  }

  _bind() {
    this.prev?.addEventListener('click', () => this.go(this.current - 1));
    this.next?.addEventListener('click', () => this.go(this.current + 1));
    this.dots.forEach(dot => dot.addEventListener('click', () => this.go(parseInt(dot.dataset.dot))));
    // Pause on hover, but only over the arrows and dots -- not the whole
    // carousel, which is what this used to listen on. In banner mode the hero
    // is ~920px tall, i.e. taller than a 900px viewport, so a desktop pointer
    // is inside it the entire time anyone is looking at the top of the
    // homepage. Autoplay was running correctly and being paused on sight.
    this.el.querySelectorAll('[data-carousel-prev], [data-carousel-next], [data-dot]').forEach(control => {
      control.addEventListener('mouseenter', () => { this.paused = true; this._stop(); });
      control.addEventListener('mouseleave', () => { this.paused = false; if (this.autoplay) this._start(); });
    });

    // Touch support
    let startX = 0;
    this.el.addEventListener('touchstart', e => { startX = e.touches[0].clientX; }, { passive: true });
    this.el.addEventListener('touchend', e => {
      const diff = startX - e.changedTouches[0].clientX;
      if (Math.abs(diff) > 50) this.go(this.current + (diff > 0 ? 1 : -1));
    });
  }

  go(index) {
    this.current = ((index % this.total) + this.total) % this.total;
    this.track.style.transform = `translateX(-${this.current * 100}%)`;
    this.slides.forEach((s, i) => s.classList.toggle('active', i === this.current));
    this.dots.forEach((d, i) => d.classList.toggle('active', i === this.current));
    if (this.autoplay && !this.paused) this._start();
  }

  // Clears first: moving between two dots fires leave-then-enter, and _start
  // is now reachable from more places than it was.
  _start() { this._stop(); this.timer = setInterval(() => this.go(this.current + 1), this.interval); }
  _stop() { clearInterval(this.timer); }
}

// ── Header: search + scroll shadow ───────────────────────────────────────────

class SiteHeader {
  constructor() {
    this.header = document.getElementById('site-header');
    this.searchToggle = document.getElementById('search-toggle');
    this.searchBar = document.getElementById('header-search');
    this.searchInput = document.getElementById('search-input');
    this._bind();
  }

  _bind() {
    // Scroll shadow
    window.addEventListener('scroll', () => {
      this.header?.classList.toggle('scrolled', window.scrollY > 10);
    }, { passive: true });

    // Search expand
    this.searchToggle?.addEventListener('click', () => {
      const open = this.searchBar?.classList.toggle('open');
      this.searchToggle.setAttribute('aria-expanded', open);
      if (open) setTimeout(() => this.searchInput?.focus(), 100);
    });

    // Close search on Escape
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && this.searchBar?.classList.contains('open')) {
        this.searchBar.classList.remove('open');
        this.searchToggle?.setAttribute('aria-expanded', 'false');
      }
    });
  }
}

// ── Mobile Menu ───────────────────────────────────────────────────────────────

class MobileMenu {
  constructor() {
    this.menu = document.querySelector('.mobile-menu');
    document.querySelector('[data-open-menu]')?.addEventListener('click', () => this.open());
    document.querySelector('[data-close-menu]')?.addEventListener('click', () => this.close());
  }
  open() { this.menu?.classList.add('open'); document.body.style.overflow = 'hidden'; }
  close() { this.menu?.classList.remove('open'); document.body.style.overflow = ''; }
}

// ── Product Form (product page) ───────────────────────────────────────────────

class ProductForm {
  constructor(el) {
    this.el = el;
    this.variantData = JSON.parse(el.querySelector('[data-variant-json]')?.textContent || '[]');
    this.selectedOptions = {};
    this.currentVariant = null;
    this._bind();
  }

  _bind() {
    // A ?variant= link -- shared by a customer, or written by _updateVariant --
    // has to open on that size. The pre-selection below used to always click the
    // first option, so a link to size 46 rendered 46 server-side and then
    // silently switched itself to 38 as soon as the JS ran.
    const wantedId = new URLSearchParams(window.location.search).get('variant');
    const wantedVariant = this.variantData.find(v => String(v.id) === wantedId);

    this.el.querySelectorAll('[data-option-name]').forEach((group, position) => {
      const name = group.dataset.optionName;
      const buttons = [...group.querySelectorAll('[data-option-value]')];
      buttons.forEach(btn => {
        btn.addEventListener('click', () => {
          buttons.forEach(b => b.classList.remove('selected'));
          btn.classList.add('selected');
          this.selectedOptions[name] = btn.dataset.optionValue;
          const label = this.el.querySelector(`[data-selected-option="${name}"]`);
          if (label) label.textContent = btn.dataset.optionValue;
          group.classList.remove('option-missing');
          this._clearPrompt();
          this._updateVariant();
        });
      });
      // Pre-select ONLY from a ?variant= link. The fallback used to be
      // `|| buttons[0]`, so a customer who never touched the size grid still
      // got size 38 in the cart the moment they clicked Add to Cart. Nothing is
      // selected now until they choose; _requireOptions() blocks the add.
      const wantedValue = wantedVariant ? wantedVariant.options[position] : null;
      buttons.find(b => b.dataset.optionValue === wantedValue)?.click();
    });

    this.el.addEventListener('submit', async e => {
      e.preventDefault();
      if (!this._requireOptions()) return;
      const btn = this.el.querySelector('[data-add-btn]');
      const qty = parseInt(this.el.querySelector('[name=quantity]')?.value || 1);
      await addToCart(this.currentVariant.id, qty, btn);
    });

    this.el.querySelector('[data-buy-now]')?.addEventListener('click', async e => {
      const btn = e.currentTarget;
      if (!this._requireOptions()) return;
      const qty = parseInt(this.el.querySelector('[name=quantity]')?.value || 1);
      await addToCart(this.currentVariant.id, qty, btn, '/checkout');
    });

    // Thumbnails
    this.el.querySelectorAll('.product-thumbnail').forEach((t, i) => {
      t.addEventListener('click', () => this._setMainImage(i));
    });
  }

  // Every option has to be chosen deliberately. Returns false and points the
  // customer at what is missing rather than falling back to a default variant.
  _requireOptions() {
    const groups = [...this.el.querySelectorAll('[data-option-name]')];
    const missing = groups.filter(g => !g.querySelector('.selected'));
    if (!missing.length && this.currentVariant) return true;

    const prompt = this.el.querySelector('[data-option-prompt]');
    const names = (missing.length ? missing : groups).map(g => g.dataset.optionName.toLowerCase());
    missing.forEach(g => g.classList.add('option-missing'));
    if (prompt) {
      prompt.textContent = names.length
        ? `Please select a ${names.join(' and a ')} first.`
        : 'That combination is unavailable — please pick another.';
      prompt.hidden = false;
    }
    (missing[0] || groups[0])?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    return false;
  }

  _clearPrompt() {
    const prompt = this.el.querySelector('[data-option-prompt]');
    if (prompt) { prompt.hidden = true; prompt.textContent = ''; }
  }

  _setMainImage(index) {
    const thumbs = this.el.querySelectorAll('.product-thumbnail');
    thumbs.forEach((t, i) => t.classList.toggle('active', i === index));
    const thumbImg = thumbs[index]?.querySelector('img');
    // Use the 800px variant, not the 144px thumbnail src, or the main image blurs.
    const src = thumbImg?.dataset.full || thumbImg?.src;
    if (src) {
      const main = this.el.querySelector('.product-gallery-main img');
      if (main) {
        main.src = src;
        main.alt = thumbImg.alt || '';
      }
    }
  }

  _updateVariant() {
    const opts = Object.values(this.selectedOptions);
    this.currentVariant = this.variantData.find(v => v.options.every((o, i) => o === opts[i])) || null;
    if (!this.currentVariant) return;

    const priceEl = this.el.querySelector('[data-variant-price]');
    const compareEl = this.el.querySelector('[data-variant-compare]');
    const addBtn = this.el.querySelector('[data-add-btn]');
    const buyBtn = this.el.querySelector('[data-buy-now]');
    const availEl = this.el.querySelector('[data-variant-availability]');
    const skuEl = this.el.querySelector('[data-variant-sku]');

    // Keep ?variant= in the URL. The page reads it on load (a shared link opens
    // on the right size), and Shopify's shop_events_listener pixel resolves the
    // viewed variant from it -- without this it threw on every product view and
    // variant-level analytics recorded nothing.
    const url = new URL(window.location.href);
    url.searchParams.set('variant', this.currentVariant.id);
    window.history.replaceState({}, '', url);

    if (priceEl) priceEl.textContent = formatMoney(this.currentVariant.price);
    if (skuEl) skuEl.textContent = this.currentVariant.sku || 'N/A';
    if (compareEl) {
      const hasCompare = this.currentVariant.compare_at_price > this.currentVariant.price;
      compareEl.textContent = hasCompare ? formatMoney(this.currentVariant.compare_at_price) : '';
      compareEl.style.display = hasCompare ? '' : 'none';
    }
    const available = this.currentVariant.available;
    if (addBtn) {
      addBtn.disabled = !available;
      addBtn.textContent = available ? 'Add to Cart' : 'Sold Out';
    }
    if (buyBtn) buyBtn.disabled = !available;
    if (availEl) availEl.textContent = available ? '✓ In Stock' : 'Out of Stock';
  }
}

// ── Qty selector ──────────────────────────────────────────────────────────────

document.addEventListener('click', e => {
  const btn = e.target.closest('[data-qty-btn]');
  if (!btn) return;
  const input = btn.closest('.qty-selector')?.querySelector('input');
  if (!input) return;
  input.value = Math.max(1, parseInt(input.value || 1) + (btn.dataset.qtyBtn === 'plus' ? 1 : -1));
});

// ── Collection: grid toggle ───────────────────────────────────────────────────

function initCollection() {
  // Grid toggle
  const grid = document.getElementById('product-grid');
  document.querySelectorAll('[data-grid]').forEach(btn => {
    btn.addEventListener('click', () => {
      const cols = btn.dataset.grid;
      grid?.setAttribute('data-cols', cols);
      document.querySelectorAll('[data-grid]').forEach(b => b.classList.toggle('active', b.dataset.grid === cols));
    });
  });
}

// ── Init ──────────────────────────────────────────────────────────────────────

document.addEventListener('DOMContentLoaded', () => {
  window.cartDrawer = new CartDrawer();
  new MobileMenu();
  new SiteHeader();
  initCollection();

  // Hero carousel
  document.querySelectorAll('[data-hero-carousel]').forEach(el => new HeroCarousel(el));

  // Product page
  const productFormEl = document.querySelector('[data-product-form]');
  if (productFormEl) new ProductForm(productFormEl);

  // Cart page qty buttons
  document.querySelectorAll('[data-cart-change]').forEach(btn => {
    btn.addEventListener('click', async () => {
      const key = btn.dataset.key;
      const qtyEl = btn.closest('[data-line]')?.querySelector('[data-qty]');
      const newQty = Math.max(0, parseInt(qtyEl?.textContent || 1) + parseInt(btn.dataset.delta));
      await Cart.change(key, newQty);
      location.reload();
    });
  });
});
